import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Smile, Palette, Check } from "lucide-react";
import { Button, Popover, PopoverContent, PopoverTrigger, cn } from "@tabipla/ui";
import { getTripCover, EMOJI_OPTIONS, GRADIENT_OPTIONS } from "@/utils/trip-cover";

interface TripCoverEditorProps {
  destination: string;
  interests: string[];
  currentEmoji: string | null;
  currentColor: string | null;
  onSave: (emoji: string, color: string) => Promise<void>;
  disabled?: boolean;
}

export function TripCoverEditor({
  destination,
  interests,
  currentEmoji,
  currentColor,
  onSave,
  disabled = false,
}: TripCoverEditorProps) {
  const { t } = useTranslation();
  const generatedCover = getTripCover(destination, interests);

  const [selectedEmoji, setSelectedEmoji] = useState(currentEmoji ?? generatedCover.emoji);
  const [selectedColor, setSelectedColor] = useState(currentColor ?? generatedCover.gradient);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const hasChanges =
    selectedEmoji !== (currentEmoji ?? generatedCover.emoji) ||
    selectedColor !== (currentColor ?? generatedCover.gradient);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(selectedEmoji, selectedColor);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSelectedEmoji(generatedCover.emoji);
    setSelectedColor(generatedCover.gradient);
  };

  return (
    <div className="space-y-4">
      {/* Preview */}
      <div
        className={cn(
          "flex aspect-[3/1] items-center justify-center rounded-lg bg-gradient-to-br",
          selectedColor
        )}
      >
        <span className="text-5xl sm:text-6xl">{selectedEmoji}</span>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Emoji Picker */}
        <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" disabled={disabled}>
              <Smile className="mr-2 h-4 w-4" />
              {t("trips.cover.emoji", "アイコン")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-2" align="start">
            <div className="grid grid-cols-8 gap-1">
              {EMOJI_OPTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    setSelectedEmoji(emoji);
                    setEmojiOpen(false);
                  }}
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-md text-xl transition-colors hover:bg-muted",
                    selectedEmoji === emoji && "bg-primary/10 ring-2 ring-primary"
                  )}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Color Picker */}
        <Popover open={colorOpen} onOpenChange={setColorOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" disabled={disabled}>
              <Palette className="mr-2 h-4 w-4" />
              {t("trips.cover.color", "背景色")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-3" align="start">
            <div className="grid grid-cols-4 gap-2">
              {GRADIENT_OPTIONS.map((gradient) => (
                <button
                  key={gradient.id}
                  onClick={() => {
                    setSelectedColor(gradient.id);
                    setColorOpen(false);
                  }}
                  className={cn(
                    "relative h-12 rounded-md bg-gradient-to-br transition-transform hover:scale-105",
                    gradient.id,
                    selectedColor === gradient.id && "ring-2 ring-primary ring-offset-2"
                  )}
                  title={gradient.name}
                >
                  {selectedColor === gradient.id && (
                    <Check className="absolute inset-0 m-auto h-5 w-5 text-white drop-shadow-md" />
                  )}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Reset button */}
        {hasChanges && (
          <Button variant="ghost" size="sm" onClick={handleReset} disabled={disabled || saving}>
            {t("trips.cover.reset", "リセット")}
          </Button>
        )}

        {/* Save button */}
        {hasChanges && (
          <Button size="sm" onClick={handleSave} disabled={disabled || saving}>
            {saving ? t("common.saving", "保存中...") : t("common.save", "保存")}
          </Button>
        )}
      </div>
    </div>
  );
}
